import { useState } from "react";

import { HttpError } from "../api/rest";
import type { AccountCohort } from "../api/types";
import { accountHint, draftProblem, shortFunder } from "../lib/accounts";
import { useAccounts } from "../store/useAccounts";

/**
 * Правка когорт аккаунтов (T5.5): имя и галочки аккаунтов.
 *
 * Список пишется ЦЕЛИКОМ одной кнопкой, а не по когорте: web_api проверяет
 * весь список до записи, и половина сохранённых правок при отказе на второй —
 * то промежуточное состояние, которого оператор не заказывал.
 *
 * Аккаунт, выбывший из состава, в когорте остаётся и показан отдельно:
 * раскрытие его пропустит, а молча выкинуть его при сохранении значило бы
 * переписать когорту за оператора.
 */
export function CohortEditor({ onClose }: { onClose: () => void }) {
  const accounts = useAccounts((s) => s.data);
  const saved = useAccounts((s) => s.cohorts);
  const saveCohorts = useAccounts((s) => s.saveCohorts);
  const [draft, setDraft] = useState<Pick<AccountCohort, "name" | "accounts">[]>(() =>
    (saved ?? []).map((c) => ({ name: c.name, accounts: [...c.accounts] })),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rows = accounts?.accounts ?? [];
  const problem = draftProblem(draft);

  function update(i: number, next: Pick<AccountCohort, "name" | "accounts">) {
    setDraft((d) => d.map((c, j) => (j === i ? next : c)));
  }

  function toggle(i: number, funder: string) {
    const c = draft[i]!;
    const accounts = c.accounts.includes(funder)
      ? c.accounts.filter((a) => a !== funder)
      : [...c.accounts, funder];
    update(i, { ...c, accounts });
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await saveCohorts(draft);
      onClose();
    } catch (err) {
      setError(err instanceof HttpError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6"
      onClick={onClose}
    >
      <div
        className="flex max-h-full w-full max-w-lg flex-col gap-3 overflow-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-5 text-xs"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-medium">Когорты аккаунтов</h2>
          <span className="text-[var(--color-muted)]">
            ордер когортой: каждый аккаунт ставит полный размер
          </span>
        </div>

        {draft.length === 0 && <p className="text-[var(--color-muted)]">Когорт нет.</p>}

        {draft.map((c, i) => {
          const gone = c.accounts.filter((f) => !rows.some((r) => r.funder === f));
          return (
            <div key={i} className="flex flex-col gap-1.5 rounded border border-[var(--color-border)] p-2">
              <div className="flex items-center gap-2">
                <input
                  value={c.name}
                  onChange={(e) => update(i, { ...c, name: e.target.value.trim().toLowerCase() })}
                  placeholder="имя: a-z, 0-9, _ -"
                  className="w-40 rounded border border-[var(--color-border)] bg-transparent px-1.5 py-0.5"
                />
                <button
                  type="button"
                  onClick={() => setDraft((d) => d.filter((_, j) => j !== i))}
                  className="ml-auto text-[var(--color-muted)] hover:text-[var(--color-sell)]"
                >
                  удалить
                </button>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {rows.map((r) => (
                  <label key={r.funder} className="flex items-center gap-1" title={accountHint(r) ?? undefined}>
                    <input
                      type="checkbox"
                      checked={c.accounts.includes(r.funder)}
                      onChange={() => toggle(i, r.funder)}
                    />
                    {r.slot}
                    {accountHint(r) && <span className="text-amber-300">*</span>}
                  </label>
                ))}
                {gone.map((f) => (
                  <label key={f} className="flex items-center gap-1 text-[var(--color-muted)]">
                    <input type="checkbox" checked onChange={() => toggle(i, f)} />
                    {shortFunder(f)} — вне состава
                  </label>
                ))}
              </div>
            </div>
          );
        })}

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setDraft((d) => [...d, { name: "", accounts: [] }])}
            className="rounded border border-[var(--color-border)] px-2 py-1"
          >
            + когорта
          </button>
          {(problem ?? error) && (
            <span className="min-w-0 truncate text-[var(--color-sell)]" title={problem ?? error ?? ""}>
              {problem ?? error}
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-muted)]"
          >
            отмена
          </button>
          <button
            type="button"
            disabled={busy || problem !== null}
            onClick={() => void save()}
            className="rounded border border-[var(--color-accent)] px-2 py-1 text-[var(--color-accent)] disabled:opacity-40"
          >
            сохранить
          </button>
        </div>
      </div>
    </div>
  );
}
